import { BarChart3, Users, Tags, Car, SlidersHorizontal, Fuel } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import OversiktTab from './OversiktTab'
import ForhandlereTab from './ForhandlereTab'
import MerkerTab from './MerkerTab'
import SporringTab from './SporringTab'
import DrivlinjerTab from './DrivlinjerTab'
import SubTabBar from '../SubTabBar'
import { useBruktbilAvansert } from '../tilgang'

// Second-level tabs under Bruktbil → Ståtid. The active tab lives in the
// page-local `tab` URL param (same mechanism as Merke & modell's tabs).
// `avansert` sub-tabs need usedcartransfer_advanced (#673).
const SUB_TABS = [
  { key: 'oversikt', labelKey: 'page.tabs.oversikt', icon: BarChart3, avansert: false },
  { key: 'forhandlere', labelKey: 'page.tabs.forhandlere', icon: Users, avansert: true },
  { key: 'merker', labelKey: 'page.tabs.merker', icon: Tags, avansert: false },
  // Every model across merker, without going through the merke first (#695).
  { key: 'modeller', labelKey: 'page.tabs.modeller', icon: Car, avansert: false },
  { key: 'sporring', labelKey: 'page.tabs.sporring', icon: SlidersHorizontal, avansert: true },
  // Ståtid per drivlinje over time (#800); no dealer data, so basic tier.
  { key: 'drivlinjer', labelKey: 'page.tabs.drivlinjer', icon: Fuel, avansert: false },
] as const

type SubTab = (typeof SUB_TABS)[number]['key']

export default function StaatidPage() {
  const { t } = useTranslation('staatid')
  const { tab } = useBruktbilFilters()
  const [, setSearchParams] = useSearchParams()
  const avansert = useBruktbilAvansert()
  const tabs = SUB_TABS
    .filter(s => avansert || !s.avansert)
    .map(({ key, labelKey, icon }) => ({ key, icon, label: t(labelKey) }))
  // An advanced-only ?tab= from a basic user falls back to oversikt (#673 AC3).
  const active: SubTab = (tabs.some(s => s.key === tab) ? tab : 'oversikt') as SubTab

  function changeTab(key: SubTab) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', key)
      // Merke & modell and Modeller share the merkeDetalj/modellDetalj drill
      // params; a page opened on one must not reopen on the other (#695).
      if (active === 'modeller' || key === 'modeller') {
        next.delete('merkeDetalj')
        next.delete('modellDetalj')
      }
      return next
    })
  }

  return (
    <div className="space-y-4" data-testid="staatid-page">
      <div>
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('page.title')}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('page.intro')}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {t('page.source')}
        </p>
      </div>

      {/* Both "Oversikt" and "Merke & modell" also exist as top-level tabs, so
          this bar has to be visibly a level down — see SubTabBar. */}
      <SubTabBar
        parent={t('page.title')}
        tabs={tabs}
        active={active}
        onChange={changeTab}
        testIdPrefix="staatid-tab-"
      />

      {active === 'oversikt' && <OversiktTab />}
      {active === 'forhandlere' && <ForhandlereTab />}
      {active === 'merker' && <MerkerTab />}
      {active === 'modeller' && <MerkerTab gruppering="modell" />}
      {active === 'sporring' && <SporringTab />}
      {active === 'drivlinjer' && <DrivlinjerTab />}
    </div>
  )
}
